import React, { useEffect, useState } from "react"
import { FeedSpinner } from "@components/shared/Spinner"
import { RequestError } from "@components/shared/RequestError"
import { OutlineButton } from "@components/shared/Button"
import { usePerformersPaged } from "@hooks/usePerformersPaged"
import {
  downloadPerformersCsv,
  PerformerSortBy,
  PerformerSortDir,
} from "@requests/performers"
import Pagination from "../../components/shared/Pagination"
import { formatInterestCount } from "./formatInterestCount"

interface PerformersProps {
  bandId: number
}

interface PerformerShow {
  post_id: number
  path: string | null
  title: string
  date: string | null
  tickets_sold: number
  interest_count: number | null
  interest_percentage: number | null
  extrapolated_tickets_sold: number | null
}

interface Performer {
  band_id: number | null
  name: string
  email: string | null
  bandnada_url: string | null
  shows_count: number
  last_show_post_id: number
  last_show_path: string | null
  last_show_date: string | null
  last_show_title: string
  last_show_tickets_sold: number
  last_show_interest_count: number | null
  last_show_interest_percentage: number | null
  last_show_extrapolated_tickets_sold: number | null
  shows: PerformerShow[]
}

interface TableHeaderProps {
  sortBy: PerformerSortBy
  sortDir: PerformerSortDir
  onSort: (column: PerformerSortBy) => void
}

const SortIndicator: React.FC<{ active: boolean; dir: PerformerSortDir }> = ({
  active,
  dir,
}) => (active ? <span className="ml-1">{dir === "asc" ? "▲" : "▼"}</span> : null)

const sortableHeaderClass = (active: boolean) =>
  active
    ? "cursor-pointer select-none font-bold text-green-600"
    : "cursor-pointer select-none font-normal text-green-400"

const TableHeader: React.FC<TableHeaderProps> = ({ sortBy, sortDir, onSort }) => (
  <li className="grid grid-cols-12 lg:grid-cols-24 items-center font-bold uppercase border-b-2 border-black text-xs leading:none">
    <div
      className={`col-span-4 lg:col-span-5 ${sortableHeaderClass(sortBy === "name")}`}
      onClick={() => onSort("name")}
    >
      Band
      <SortIndicator active={sortBy === "name"} dir={sortDir} />
    </div>
    <div className="hidden lg:block lg:col-span-4">Email</div>
    <div className="col-span-2">Shows</div>
    <div
      className={`col-span-6 lg:col-span-5 ${sortableHeaderClass(sortBy === "last_show")}`}
      onClick={() => onSort("last_show")}
    >
      Last Show
      <SortIndicator active={sortBy === "last_show"} dir={sortDir} />
    </div>
    <div
      className={`hidden lg:block lg:col-span-2 ${sortableHeaderClass(sortBy === "tickets_sold")}`}
      onClick={() => onSort("tickets_sold")}
    >
      Tickets Sold
      <SortIndicator active={sortBy === "tickets_sold"} dir={sortDir} />
    </div>
    <div className="hidden lg:block lg:col-span-3">Interested Count</div>
    <div className="hidden lg:block lg:col-span-3">Extrapolated Sold</div>
  </li>
)

const ShowSubRow: React.FC<{ show: PerformerShow }> = ({ show }) => {
  const [selected, setSelected] = useState(false)

  return (
    <li
      className={`grid grid-cols-12 lg:grid-cols-24 items-center border-b border-gray-200 border-l-2 border-l-green-600 py-1.5 text-xs text-gray-700 cursor-pointer ${
        selected ? "bg-gray-300" : "bg-gray-100 hover:bg-gray-200"
      }`}
      onClick={() => setSelected((s) => !s)}
    >
      <div className="col-span-1 lg:col-span-5" />
      <div className="hidden lg:block lg:col-span-4" />
      <div className="col-span-1 lg:col-span-2" />
      <div className="col-span-4 lg:col-span-5">
        {show.date && (
          <span className="text-gray-500 mr-2">
            {new Date(show.date).toLocaleDateString()}
          </span>
        )}
        {show.path ? (
          <a
            href={show.path}
            className="hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {show.title}
          </a>
        ) : (
          <span>{show.title}</span>
        )}
      </div>
      <div className="hidden lg:block lg:col-span-2">
        <div className="text-[10px] uppercase text-gray-400">Tickets Sold</div>
        {show.tickets_sold}
      </div>
      <div className="col-span-3 lg:col-span-3">
        <div className="text-[10px] uppercase text-gray-400">Interested</div>
        {formatInterestCount(show.interest_count, show.interest_percentage)}
      </div>
      <div className="col-span-3 lg:col-span-3">
        <div className="text-[10px] uppercase text-gray-400">Extrap. Sold</div>
        {show.extrapolated_tickets_sold ?? "—"}
      </div>
    </li>
  )
}

const PerformerRow: React.FC<{ performer: Performer }> = ({ performer }) => {
  const [expanded, setExpanded] = useState(false)
  const expandable = performer.shows.length > 0

  return (
    <>
      <li
        className={`grid grid-cols-12 lg:grid-cols-24 items-center border-b border-gray-200 py-2 text-sm ${
          expandable ? "cursor-pointer" : ""
        } ${expanded ? "bg-gray-100" : expandable ? "hover:bg-gray-50" : ""}`}
        onClick={() => expandable && setExpanded((e) => !e)}
      >
        <div className="col-span-4 lg:col-span-5 font-bold flex items-center gap-1 min-w-0">
          <span
            className={`inline-block w-3 shrink-0 text-gray-400 transition-transform ${
              expanded ? "rotate-90" : ""
            }`}
          >
            {expandable ? "›" : ""}
          </span>
          <span className="truncate">{performer.name}</span>
          {performer.bandnada_url && (
            <a
              href={performer.bandnada_url}
              className="shrink-0 text-xs font-normal text-gray-400 hover:text-gray-700"
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              title={`View ${performer.name} on bandNada`}
              aria-label={`View ${performer.name} on bandNada`}
            >
              ↗
            </a>
          )}
        </div>
        <div className="hidden lg:block lg:col-span-4 truncate">
          {performer.email ? (
            <a
              href={`mailto:${performer.email}`}
              className="text-xs text-gray-600 hover:text-gray-800 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {performer.email}
            </a>
          ) : null}
        </div>
        <div className="col-span-2">{performer.shows_count}</div>
        <div className="col-span-6 lg:col-span-5">
          {performer.last_show_date && (
            <div className="text-xs text-gray-600">
              {new Date(performer.last_show_date).toLocaleDateString()}
            </div>
          )}
          {performer.last_show_path ? (
            <a
              href={performer.last_show_path}
              className="hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {performer.last_show_title}
            </a>
          ) : (
            <span>{performer.last_show_title}</span>
          )}
        </div>
        <div className="hidden lg:block lg:col-span-2">
          {performer.last_show_tickets_sold}
        </div>
        <div className="hidden lg:block lg:col-span-3">
          {formatInterestCount(
            performer.last_show_interest_count,
            performer.last_show_interest_percentage,
          )}
        </div>
        <div className="hidden lg:block lg:col-span-3">
          {performer.last_show_extrapolated_tickets_sold ?? "—"}
        </div>
      </li>
      {expanded &&
        performer.shows.map((show) => (
          <ShowSubRow key={show.post_id} show={show} />
        ))}
    </>
  )
}

export const Performers: React.FC<PerformersProps> = ({ bandId }) => {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [isExportingCsv, setIsExportingCsv] = useState(false)
  const [sortBy, setSortBy] = useState<PerformerSortBy>("last_show")
  const [sortDir, setSortDir] = useState<PerformerSortDir>("desc")

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  const handleSort = (column: PerformerSortBy) => {
    setPage(1)
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortBy(column)
      setSortDir(column === "name" ? "asc" : "desc")
    }
  }

  const { data, status, error, isPreviousData } = usePerformersPaged(
    bandId,
    page,
    50,
    debouncedSearch,
    sortBy,
    sortDir,
  )

  return (
    <div>
      <h2 className="lg:hidden font-bold text-xl uppercase border-b-4 border-black mb-4">
        Performers
      </h2>
      <div className="flex flex-wrap items-center justify-between gap-2 mt-4 mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          placeholder="Find a band..."
          className="border border-gray-300 rounded px-3 py-1.5 text-sm w-64 focus:outline-none focus:border-gray-500"
        />
        {data?.total_count > 0 && (
          <OutlineButton
            type="button"
            disabled={isExportingCsv}
            onClick={async () => {
              setIsExportingCsv(true)
              try {
                await downloadPerformersCsv(bandId)
              } finally {
                setIsExportingCsv(false)
              }
            }}
            className="text-gray-800 text-sm"
          >
            {isExportingCsv ? "Exporting..." : "Export to CSV"}
          </OutlineButton>
        )}
      </div>

      {status === "loading" || isPreviousData ? (
        <FeedSpinner />
      ) : status === "error" ? (
        <RequestError error={error} />
      ) : data ? (
        data.performers.length === 0 ? (
          <div className="text-sm text-gray-600 my-3">
            {debouncedSearch
              ? `No performers matching "${debouncedSearch}".`
              : "No one's performed at one of your shows yet."}
          </div>
        ) : (
          <>
            <ul className="lg:text-base text-xs">
              <TableHeader sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              {data.performers.map((performer: Performer) => (
                <PerformerRow
                  key={performer.band_id ?? `pending-${performer.email ?? performer.name}`}
                  performer={performer}
                />
              ))}
            </ul>
            <Pagination
              page={data.page}
              totalPages={data.total_pages}
              totalCount={data.total_count}
              hasNextPage={data.has_next_page}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => p + 1)}
            />
          </>
        )
      ) : null}
    </div>
  )
}

export default Performers
